import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { AppContextType, ColumnInfo, Record, TranscriptsCache } from '../types';

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [records, setRecords] = useState<Record[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [transcriptsCache, setTranscriptsCache] = useState<TranscriptsCache | null>(null);

  useEffect(() => {
    // Fetch columns and records when component mounts
    fetchTableColumns();
    fetchTableRecords();
  }, []);

  const fetchTableColumns = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_columns', { table_name: 'content_blocks' });
      
      if (error) throw error;
      
      setColumns(data || []);
    } catch (err: any) {
      setError(err.message);
      toast.error(`Failed to fetch columns: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchTableRecords = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('content_blocks')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setRecords(data || []);
    } catch (err: any) {
      setError(err.message);
      toast.error(`Failed to fetch records: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await fetchTableRecords();
  };

  return (
    <AppContext.Provider
      value={{
        columns,
        setColumns,
        records,
        setRecords,
        refreshData,
        error,
        setError,
        loading,
        setLoading,
        transcriptsCache,
        setTranscriptsCache,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};